import React from 'react';
import { CalendarClock, Inbox, MessagesSquare, Bot, Compass, Brain, Sparkles, Settings, Waves, Heart } from 'lucide-react';

export const HELP_SECTIONS = [
  {
    id: 'start', icon: Heart, title: 'Getting started',
    intro: "Welcome! Larkwell works with your brain, not against it. No need to do everything \u2014 start with one thing.",
    items: [
      'Sign in with Google or email \u2014 your workspace and a personal "Larkwell Coach" are set up automatically.',
      'Peek at Settings once to make it comfy: Low-stim mode, dyslexia-friendly font, text size, and light/dark mode.',
      'There is no wrong way to use Larkwell. Poke around \u2014 nothing here can break.',
    ],
  },
  {
    id: 'today', icon: CalendarClock, title: 'Planning your day',
    intro: 'The Today page turns a vague, heavy day into a gentle, visual plan.',
    items: [
      'Tap "Plan My Day" and Larkwell builds a realistic, break-friendly schedule for you.',
      'Feeling off-track? "Replan My Day" reshuffles what is left \u2014 no guilt.',
      'Watch the Capacity Meter so you do not over-commit; it is okay to do less.',
      'Start a Focus sprint when you are ready \u2014 gentle alerts ease you in and out, and wins are logged.',
    ],
  },
  {
    id: 'tasks', icon: Inbox, title: 'Capturing tasks (brain-dump)',
    intro: 'Get it out of your head first; organise later.',
    items: [
      'Dump anything into the inbox the moment you think of it.',
      'Overwhelmed by a big task? Ask Larkwell to break it into tiny, doable steps.',
      'Turn on Nag mode for kind nudges when you want a little accountability.',
    ],
  },
  {
    id: 'chats', icon: MessagesSquare, title: 'Chats & agents',
    intro: 'Talk things through with AI helpers \u2014 solo or in a group.',
    items: [
      'Chat 1:1 with your Larkwell Coach anytime for planning, focus, or a pep talk.',
      'Add multiple agents to one chat and @-mention the one you want to answer.',
      'Use voice: dictate hands-free, have replies read aloud, or open the full Call screen.',
      'Each agent can have its own voice and speaking speed (set it in the agent builder).',
    ],
  },
  {
    id: 'agents', icon: Bot, title: 'Building your own agents',
    intro: 'Make a helper that fits exactly how you work.',
    items: [
      'Give it a name, personality, avatar, and pick which AI model powers it.',
      'Attach knowledge files so it answers with your context.',
      'Publish it to Discover, or browse and "remix" others\u2019 agents into your workspace.',
    ],
  },
  {
    id: 'memory', icon: Brain, title: 'Memory that sticks',
    intro: 'Larkwell remembers what matters so you never repeat yourself.',
    items: [
      'It quietly saves key facts (your name, preferences, ongoing projects) across chats.',
      'Review, edit, or delete anything on the Memory page \u2014 you are always in control.',
      'New chats start \u201cwarm\u201d, already knowing your context.',
    ],
  },
  {
    id: 'connect', icon: Settings, title: 'Connecting calendar & messaging',
    intro: 'Optional, but this is where Larkwell feels like magic.',
    items: [
      'Calendar: connect Google or Outlook in Settings for two-way sync with your real events.',
      'Messaging: add your own Twilio or Telegram keys to text your coach and get nudges.',
      'Skills: give agents extra abilities (playbooks, webhooks, MCP tools) from the Skills page.',
    ],
  },
  {
    id: 'access', icon: Waves, title: 'Accessibility (always free)',
    intro: 'These never cost anything, on any plan.',
    items: [
      'Low-stimulation mode dials down motion and visual noise.',
      'Dyslexia-friendly font and adjustable text size.',
      'Warm light and dark themes \u2014 pick what feels calm.',
    ],
  },
];

export const TESTER_SECTION = {
  id: 'testers', icon: Sparkles, title: 'For testers \u2014 thank you! \ud83d\udc9b',
  intro: "You have free lifetime Pro. Here is what would help most, and how to send feedback.",
  items: [
    'Redeem your invite: go to Upgrade \u2192 \u201cHave an invite code?\u201d \u2192 paste your LARKVIP code \u2192 Redeem.',
    'Try the core loop: brain-dump a few tasks, hit \u201cPlan My Day\u201d, and run one Focus sprint.',
    'Have a real chat with your Larkwell Coach and try voice or a Call.',
    'Notice: what felt genuinely helpful? What was confusing? What made you want to keep going (or stop)?',
    'Send feedback: just reply to my email, or use the in-app chat \u2014 even a few lines is gold.',
  ],
};

export const HelpContent = ({ includeTesters = true }) => {
  const sections = includeTesters ? [...HELP_SECTIONS, TESTER_SECTION] : HELP_SECTIONS;
  return (
    <div className="space-y-5" data-testid="help-content">
      {sections.map((s) => {
        const Icon = s.icon;
        const isTester = s.id === 'testers';
        return (
          <section key={s.id} id={s.id} className="rounded-2xl border p-5 bg-card" style={isTester ? { borderColor: 'hsl(var(--primary) / 0.4)' } : { borderColor: 'hsl(var(--border))' }} data-testid={`help-section-${s.id}`}>
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: isTester ? 'hsl(var(--primary))' : 'hsl(var(--accent) / 0.15)' }}>
                <Icon className="h-4.5 w-4.5" style={{ color: isTester ? '#ffffff' : 'hsl(var(--primary))' }} />
              </div>
              <h2 className="font-display text-lg font-700">{s.title}</h2>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{s.intro}</p>
            <ul className="space-y-2">
              {s.items.map((it, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'hsl(var(--primary))' }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default HelpContent;
